import _ from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { generateClient } from '@aws-amplify/api';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as icons from '@fortawesome/free-regular-svg-icons';
import * as icons_solid from '@fortawesome/free-solid-svg-icons';
import { getQuestionnaires, getSubjectAnswers } from '@/graphql/queries';
import { upsertQuestionnaire } from '@/graphql/mutations';
import useBodyClick from '@/hooks/useBodyClick';
import useArrowKeys from '@/hooks/useArrowKeys';
import useLoadMore from '@/hooks/useLoadMore';
import useSubscription from '@/hooks/useSubscription';
import Header from '@/Header';
import { dash, downIcon, SpinnerLineDark, upIcon } from '@/utils';
import { getListSort, setListSort } from '@/utils/stickySettings';

const client = generateClient();
const PURPLE = "#2E2868";

export default () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [busy, setBusy] = useState({});
  
  const updateBusy = useCallback((id, value) => {
    setBusy(prevState => ({ ...prevState, [id]: value }));
  }, []);

  const [search, setSearch] = useState("");
  const [searchCount, setSearchCount] = useState(0);
  const incSearchCount = useCallback(() => setSearchCount((p) => p + 1), []);
  const debounceSearch = useCallback(_.debounce((v) => { setDefaults(); setSearch(v) }, 500), []);

  const { field: defaultSortField, order: defaultSortOrder } = useMemo(() => getListSort({ key: window.location.pathname }) || { field: 'updated_at', order: 'desc' }, []);
  const [sortField, setSortField] = useState(defaultSortField && defaultSortOrder ? defaultSortField : undefined);
  const [sortOrder, setSortOrder] = useState(defaultSortField && defaultSortOrder ? { [defaultSortField]: defaultSortOrder } : {});

  const filters_ref = useRef();
  filters_ref.current = useMemo(() => {
    const filters = [];
    const q = (search || '').trim();
    if (q.length > 0) {
      const n = Number(q);
      filters.push(
        ...(!isNaN(n) ? [{ id: { eq: n } }] : []),
        { name: { like: `%${q}%` } },
        { description: { like: `%${q}%` } }
      );
    }
    const sorting = [
      { field: sortField, order: sortOrder[sortField] },
      { field: 'id', order: 'asc' }
    ];
    return {
      filters,
      sorting
    };
  }, [search, sortField, sortOrder]);

  const { isLoadingMore, limit, loadMoreButton, offset, rows, setDefaults, setIsLoadingMore, setRows, setTotal } = useLoadMore({ position: 'absolute' });
  useSubscription({ filters_ref, limit_ref: limit, offset_ref: offset, rankings_key: 'questionnaires', setRows });

  
  const [sidebarItem, _setSidebarItem] = useState();
  const [sidebarSubjectCount, setSidebarSubjectCount] = useState(null);

  const setSidebarItem = useCallback(item => {
    _setSidebarItem(item);
    const queryParams = new URLSearchParams(window.location.search);
    if (!item) {
      queryParams.delete('questionnaire_id');
    } else {
      queryParams.set('questionnaire_id', item.id);
    }
    navigate({ pathname: window.location.pathname, search: `?${queryParams.toString()}` }, { replace: true });
  }, []);

  const [loadCount, setLoadCount] = useState(0);
  const incLoadCount = () => setLoadCount(prevState => prevState + 1);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const q_id = parseInt(queryParams.get('questionnaire_id') || undefined, 10);
    if (q_id) {
      const item = rows.find(e => e.id === q_id);
      if (item) setSidebarItem(item);
    }
  }, [rows]);

  
  useEffect(() => {
    if (sidebarItem?.id) {
      const fetchSubjectCount = async () => {
        setSidebarSubjectCount(null);
        try {
          const res = await client.graphql({
            query: getSubjectAnswers,
            variables: {
              filters: { questionnaire_id: { eq: sidebarItem.id } }
            }
          });
          const answers = res?.data?.getSubjectAnswers?.subject_answers || [];
          const uniqueSubjectIds = _.uniqBy(answers, 'subject_id');
          setSidebarSubjectCount(uniqueSubjectIds.length);
        } catch (err) {
          console.error("Error calculating subject count", err);
          setSidebarSubjectCount(0);
        }
      };
      fetchSubjectCount();
    }
  }, [sidebarItem?.id]);


  const load = useCallback(async () => {
    const { filters, sorting } = filters_ref.current;
    try {
      const res = await client.graphql({
        query: getQuestionnaires,
        variables: { filters, limit, offset, sorting }
      });

      const payload = res?.data?.getQuestionnaires;
      const data = payload?.questionnaires ?? [];
      
      setRows(prevState => (offset === 0 ? data : [...prevState, ...data]));
      setTotal(payload?.total_count ?? data.length);
      incLoadCount();
      setIsLoadingMore(false);
    } catch (err) {
      console.error("Error loading questionnaires", err);
      setIsLoadingMore(false);
    }
  }, [filters_ref.current, limit, offset]);

  useEffect(() => {
    load().catch(console.error);
  }, [load]);

  useBodyClick({ onClick: setSidebarItem });
  useArrowKeys({ rows, setSidebarRow: setSidebarItem, sidebarRow: sidebarItem });

  const handleEdit = (id) => {
    navigate(`/app/customers/questionnaires/${id}/questions`);
  };

  const handleDelete = useCallback(async (row) => {
    const ok = window.confirm(`Delete questionnaire "${row.name}"?`);
    if (!ok) return;
    try {
      updateBusy(row.id, true);
      if (row.id === sidebarItem?.id) {
        setSidebarItem();
      }
      await client.graphql({
        query: upsertQuestionnaire,
        variables: {
          input: { questionnaires: { id: row.id } },
          input_delete: [{ id: row.id, key: "questionnaires" }],
        },
      });
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      setTotal((t) => Math.max(0, t - 1));
    } catch (e) {
      console.error(e);
      alert('Delete failed');
    } finally {
      updateBusy(row.id, false);
    }
  }, [sidebarItem]);

  const handleSort = field => () => {
    if (sortField === field) {
      setSortOrder(prevState => ({ ...prevState, [sortField]: prevState[sortField] === 'asc' ? 'desc' : 'asc' }));
    } else {
      setSortOrder(prevState => ({ ...prevState, [field]: prevState[field] || 'asc' }));
      setSortField(field);
    }
    setDefaults();
  };

  const sortIcon = sortOrder[sortField] === 'asc' ? upIcon : downIcon;

  // ------- Styles & Breakout -------
  const stickyThStyle = {
    position: "sticky",
    top: 0,
    backgroundColor: "#F4F4F4",
    zIndex: 2,
    fontSize: "0.875rem",
    textAlign: "left"
  };

  const idCellStyle = { borderRight: "1px solid #ccc", padding: "0 12px", maxWidth: 90  };
  const nameCellStyle = { borderRight: "1px solid #ccc", padding: "0 12px", maxWidth: 250 }; 
  const descCellStyle = { borderRight: "1px solid #ccc", padding: "0 12px", maxWidth: 250 };                                 
  const lastCellStyle = { padding: "0 12px", maxWidth: 240 };                                 

  const questionnaire_breakout = item => {
    return (
      <div onClick={event => event.stopPropagation()}>
        {/* Title Link - Updated to /questions */}
        <Link
          to={`/app/customers/questionnaires/${item.id}/questions`}
          className="text-decoration-none fg-purple"
         >
          <h5 className="mb-3 fg-purple text-break"><b>{item.name}</b></h5>
        </Link>
        <div className="mt-4">
             <div className="row g-0 mb-2">
               <div className="ms-auto col-auto"><u><b>Count</b></u></div>
             </div>

             <Link
               className="text-decoration-none"
               style={{ color: "inherit" }}
               to={`/app/customers/questionnaires/subjects?questionnaire_id=${item.id}&questionnaire_name=${encodeURIComponent(item.name || "")}&questionnaire_outcome=Abandoned`}
             >
                <div className="row g-0 mb-2">
                    <div className="col">Abandoned Subjects</div>
                    <div className="col-auto">
                        {item.subject_abandoned_count}
                    </div>
                </div>
             </Link>

             <Link
               className="text-decoration-none"
               style={{ color: "inherit" }}
               to={`/app/customers/questionnaires/subjects?questionnaire_id=${item.id}&questionnaire_name=${encodeURIComponent(item.name || "")}&questionnaire_outcome=Complete`}
             >
                <div className="row g-0 mb-2">
                    <div className="col">Complete Subjects</div>
                    <div className="col-auto">
                        {item.subject_complete_count}
                    </div>
                </div>
             </Link>

             <Link
               className="text-decoration-none"
               style={{ color: "inherit" }}
               to={`/app/customers/questionnaires/subjects?questionnaire_id=${item.id}&questionnaire_name=${encodeURIComponent(item.name || "")}&questionnaire_outcome=Eligible`}
             >
                <div className="row g-0 mb-2">
                    <div className="col">Eligible Subjects</div>
                    <div className="col-auto">
                        {item.subject_eligible_count}
                    </div>
                </div>
             </Link>

             <Link
               className="text-decoration-none"
               style={{ color: "inherit" }}
               to={`/app/customers/questionnaires/subjects?questionnaire_id=${item.id}&questionnaire_name=${encodeURIComponent(item.name || "")}&questionnaire_outcome=Ineligible`}
             >
                <div className="row g-0 mb-2">
                    <div className="col">Ineligible Subjects</div>
                    <div className="col-auto">
                        {item.subject_ineligible_count}
                    </div>
                </div>
             </Link>

             <Link
               className="text-decoration-none"
               style={{ color: "inherit" }}
               to={`/app/customers/questionnaires/subjects?questionnaire_id=${item.id}&questionnaire_name=${encodeURIComponent(item.name || "")}`}
             >
                <div className="row g-0 mb-2">
                    <div className="col"><b>Total Subjects</b></div>
                    <div className="col-auto">
                        {item.subject_count}
                    </div>
                </div>
             </Link>
        </div>
      </div>
    );
  };

  const table = useMemo(() => (
    <div className="row g-0">
      <div className="col pe-0">
        <div className="border rounded-3 overflow-auto" style={{ maxHeight: `calc(100vh - 150px)` }}>
          <table
            className="table table-sm table-borderless align-middle mb-0"
            onClick={event => event.stopPropagation()}
            style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.875rem", tabIndex: "0", tableLayout: "auto" }}
          >
            <thead className="text-muted small" style={{ height: "30px" }}>
              <tr className="border-bottom">
                <th style={stickyThStyle} className="text-start align-middle py-2 ps-3 cursor-pointer" onClick={handleSort('id')}>ID{sortField === 'id' && sortIcon}</th>
                <th style={stickyThStyle} className="text-start align-middle py-2 ps-3 cursor-pointer" onClick={handleSort('name')}>Name{sortField === 'name' && sortIcon}</th>
                <th style={stickyThStyle} className="text-start align-middle py-2 ps-3 cursor-pointer" onClick={handleSort('description')}>Description{sortField === 'description' && sortIcon}</th>
                <th style={stickyThStyle} className="text-start align-middle py-2 ps-3 cursor-pointer" onClick={handleSort('updated_at')}>Updated At{sortField === 'updated_at' && sortIcon}</th>
              </tr>
            </thead>

            <tbody>
              {rows.map((r) => {
                return (
                  <tr
                    className={`position-relative cursor-default border-bottom ${sidebarItem?.id === r.id ? 'bw-focus-ring' : ''}`}
                    key={r.id}
                    onClick={event => { event.stopPropagation(); setSidebarItem(r); }}
                    style={{ height: "30px", tabIndex: 0 }}
                  >
                    <td className="text-start align-middle py-2 ps-3" style={idCellStyle}>
                      {/* ID Link - Updated to /questions */}
                      <Link
                        to={`/app/customers/questionnaires/${r.id}/questions`}
                        className="text-decoration-none"
                        style={{ color: "inherit" }}
                      >
                        {r.id}
                        {busy[r.id] ? (
                          <SpinnerLineDark />
                        ) : undefined}
                      </Link>
                    </td>

                    <td className="text-start align-middle py-2 px-3 text-break" style={nameCellStyle}>
                      {/* Name Link - Updated to /questions */}
                      <Link
                        to={`/app/customers/questionnaires/${r.id}/questions`}
                        className="text-decoration-none fg-purple"
                        style={{ color: PURPLE }}
                      >
                        <b>{r.name || dash}</b>
                      </Link>
                    </td>

                    <td className="text-start align-middle py-2 ps-3 text-break" style={descCellStyle}>
                      {r.description || dash}
                    </td>

                    <td className="align-middle py-2 ps-3" style={lastCellStyle}>
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="text-muted small">
                          {r.updated_at ? moment(r.updated_at).fromNow() : "—"}
                        </span>

                        <div className="d-flex align-items-center gap-3">
                          <FontAwesomeIcon
                            className="cursor-pointer"
                            style={{ color: "var(--bs-body-color)" }}
                            icon={icons.faPenToSquare}
                            title="Edit Questions"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(r.id);
                            }}
                          />
                          <FontAwesomeIcon
                            className="cursor-pointer text-danger"
                            icon={icons.faTrashCan}
                            onClick={event => { event.stopPropagation(); handleDelete(r); }}
                            title="Delete"
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {sidebarItem && (
        <div className="col-auto ps-4 width_300">
          <div className="position-sticky" style={{maxHeight: '100vh', overflow: 'auto', top: '0px'}}>
            <button aria-label="Close" className="btn-close float-end" onClick={() => setSidebarItem()} type="button"></button>
              {questionnaire_breakout(sidebarItem)}
          </div>
        </div>
      )}
    </div>
  ), [loadCount, sidebarItem, isLoadingMore, JSON.stringify(busy), rows, sortField, sortOrder, sidebarSubjectCount]);

  return (
    <div className="container-fluid px-4">
      <Header path="/app/customers/">
        <div className="row align-items-center mt-3 mb-3 g-0">
          <div className="col">
            <h4 className="mb-0 ps-4" style={{ color: PURPLE }}>Questionnaires</h4>
          </div>
          <div className="col-auto ms-auto pe-0 position-relative">
            <input
              autoFocus
              className="form-control width_200"
              defaultValue={search}
              key={searchCount}
              placeholder="Search..."
              onChange={(e) => debounceSearch(e.target.value)}
            />
            <i
              className="bi bi-x position-absolute top-50 end-0 translate-middle-y me-3 text-muted"
              onClick={() => {
                setSearch("");
                incSearchCount();
              }}
              role="button"
            ></i>
          </div>

          <div className="col-auto">
            <Link to="/app/customers/questionnaires/setup">
              <button
                className="btn btn-primary"
                style={{ backgroundColor: PURPLE, borderColor: PURPLE, marginLeft: "20px" }}
              >
                Create New Questionnaire
              </button>
            </Link>
          </div>
        </div>
        <hr />
      </Header>

      {rows?.length ? (
        <AnimatePresence initial={true}>
          <motion.div
            key="content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1.0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
          >
            {table}
            {loadMoreButton}
          </motion.div>
        </AnimatePresence>
      ) : undefined}

    </div>
  );
};